import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/testna_postaja/tretiranje";

export const fetchTreatmentPage = createAsyncThunk(
  "active-substance/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        false
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchTopActiveSubstances = createAsyncThunk(
  "tretiranje/topLatestActiveSubstances",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/page`, undefined, {
        page: 0,
        size: 30,
      });

      const content = response.data?.content ?? [];

      const counts = content.reduce((acc, item) => {
        const u = item.ucinkovina;
        if (!u?.id) return acc;

        if (!acc[u.id]) {
          acc[u.id] = {
            ...u,
            count: 0,
          };
        }

        acc[u.id].count += 1;
        return acc;
      }, {});

      return Object.values(counts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map(({ count, ...rest }) => rest);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createTreatment = createAsyncThunk(
  "treatment/create",
  async (treatment, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(baseUrl, treatment);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createGroupTreatment = createAsyncThunk(
  "treatment/createGroup",
  async (treatment, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`${baseUrl}/skupina`, treatment);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const treatmentSlice = createSlice({
  name: "treatment",
  initialState: {
    page: {},
    topActiveSubstances: [],
    groupPage: null,
    details: null,
    error: null,
    isLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTreatmentPage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTreatmentPage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })
      .addCase(fetchTreatmentPage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTopActiveSubstances.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTopActiveSubstances.fulfilled, (state, action) => {
        state.isLoading = false;
        state.topActiveSubstances = action.payload;
      })
      .addCase(fetchTopActiveSubstances.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createTreatment.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(createTreatment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createTreatment.pending, (state, _) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createGroupTreatment.fulfilled, (state, _) => {
        state.isLoading = false;
      })
      .addCase(createGroupTreatment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createGroupTreatment.pending, (state, _) => {
        state.isLoading = true;
        state.error = null;
      });
  },
});

export default treatmentSlice.reducer;
